import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut, MessageCircleHeart, Shield } from "lucide-react";
import FeedbackDialog from "@/components/FeedbackDialog";

export default function Layout({ children, showNav = true }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen texture-grain flex flex-col" data-testid="app-layout">
      {showNav && (
        <header className="z-40 border-b border-border/60 bg-background sticky top-0">
          <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <a
                href="https://www.commongroundinstitute.net"
                target="_blank"
                rel="noreferrer"
                className="shrink-0"
                aria-label="Common Ground Institute — opens in a new tab"
                data-testid="nav-logo-link"
              >
                <img
                  src="/cgi-logo.png"
                  alt="Common Ground Institute"
                  className="h-9 w-auto object-contain"
                  data-testid="nav-logo-image"
                />
              </a>
              <Link
                to={user ? "/dashboard" : "/"}
                className="font-medium tracking-tight text-foreground hidden sm:inline truncate hover:opacity-80"
                style={{ fontFamily: 'Manrope' }}
                data-testid="nav-app-name"
              >
                Conflict Resolution Assistant
              </Link>
            </div>
            {user && (
              <div className="flex items-center gap-2 sm:gap-3">
                {user.is_admin && (
                  <Button
                    variant="ghost"
                    size="sm"
                    asChild
                    className="rounded-full"
                    data-testid="admin-link"
                  >
                    <Link to="/admin">
                      <Shield className="w-4 h-4 mr-1" strokeWidth={1.5} />
                      <span className="hidden sm:inline">Admin</span>
                    </Link>
                  </Button>
                )}
                <span className="text-sm text-muted-foreground hidden sm:inline" data-testid="nav-user-name">
                  {user.name}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  data-testid="logout-button"
                  className="rounded-full"
                >
                  <LogOut className="w-4 h-4 mr-1" strokeWidth={1.5} />
                  <span className="hidden sm:inline">Sign out</span>
                </Button>
              </div>
            )}
          </div>
        </header>
      )}
      <main className="relative z-10 flex-1">{children}</main>

      {/* Persistent 'under development' feedback strip on every page */}
      <footer className="relative z-10 border-t border-border/60 bg-secondary/40 mt-8" data-testid="dev-feedback-strip">
        <div className="max-w-5xl mx-auto px-6 py-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center text-sm text-foreground/75">
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary animate-pulse" aria-hidden />
            This app is under active development.
          </span>
          <button
            type="button"
            onClick={() => setFeedbackOpen(true)}
            className="inline-flex items-center gap-1 text-primary hover:underline font-medium"
            data-testid="open-feedback-inline"
          >
            <MessageCircleHeart className="w-4 h-4" strokeWidth={1.5} />
            Please… PLEASE! Help us improve.
          </button>
        </div>
      </footer>

      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </div>
  );
}
